import type { ClaimInput } from './api';

/**
 * Known tenant claims to pick from (AUTH CLAUDE.md §4.4 scope vocabulary + the scopes each service checks). AUTH stores
 * opaque strings, so anything not listed can still be added as a custom claim. Add a row when a service starts checking
 * a new scope.
 */
export interface CatalogClaim extends ClaimInput {
  /** What holding it allows. */
  hint: string;
}
export interface CatalogGroup {
  id: string;
  label: string;
  claims: CatalogClaim[];
}
/** A typical tenant's claims, applied in one click. */
export interface ClaimPreset {
  id: string;
  label: string;
  hint: string;
  claims: ClaimInput[];
}

const scope = (value: string, hint: string): CatalogClaim => ({ type: 'scope', value, hint });

export const CLAIM_CATALOG: CatalogGroup[] = [
  {
    id: 'installation',
    label: 'WMS installation (service clients)',
    claims: [
      scope('datacollector:write', 'Send logs and statistics to the datacollector'),
      scope('boxcalc:calculate', 'Call the box calculation service'),
      scope('printqueue:enqueue', 'Send print jobs to the print queue'),
      scope('printqueue:print', 'On-prem print server: fetch and report its print jobs'),
      scope('automation-gw:location:read', 'Resolve where an automation tunnel is connected'),
      scope('automation-gw:tunnel', 'On-prem automation agent: open a tunnel'),
    ],
  },
  {
    id: 'provisioning',
    label: 'Provisioning',
    claims: [
      scope('scim:provision', 'SCIM gateway → AUTH provisioning'),
      scope('wms:userprovisioning:write', 'SCIM gateway → WMS user provisioning (legacy targets)'),
    ],
  },
  {
    id: 'apps',
    label: 'Applications',
    claims: [
      scope('nycebuddy:ask', 'nyce.buddy: ask questions'),
      scope('nycebuddy:mcp', 'nyce.buddy: the MCP endpoint (Claude and other MCP clients)'),
      scope('nycebuddy:service', 'nyce.buddy: service accounts (schema refresh, monitors)'),
      scope('nycebuddy:admin', 'nyce.buddy: administration'),
      scope('nycebuddy:usage:read', 'nyce.buddy: usage for billing (operator tenant service client)'),
    ],
  },
  {
    id: 'design',
    label: 'NYCE Design',
    claims: [
      scope('design:warehousedesign:*', 'NYCE Design: open and edit warehouse designs (customer tenants)'),
      scope('design:warehousedesign:read', 'NYCE Design: open warehouse designs (read-only)'),
      scope('design:warehousedesign:write', 'NYCE Design: edit warehouse designs'),
      scope('design:locations:write', 'NYCE Design ext API: upsert locations (the WMS integration client)'),
      scope('design:zones:write', 'NYCE Design ext API: upsert picking zones (the WMS integration client)'),
      scope(
        'design:module-types:write',
        'NYCE Design ext API: upsert module types (the WMS integration client)'
      ),
      scope(
        'design:frequency-classes:write',
        'NYCE Design ext API: upsert frequency classes (the WMS integration client)'
      ),
      scope('design:tenants:read', 'NYCE Design tenant registry (read) — operator tenant only'),
      scope('design:tenants:write', 'NYCE Design tenant registry (write) — operator tenant only'),
    ],
  },
  {
    id: 'ophub',
    label: 'Operations Hub',
    claims: [
      scope('ophub:*', 'Everything in the Operations Hub'),
      scope('ophub:gcplogs:read', 'Cloud Logging'),
      scope('ophub:pods:read', 'Pods (read)'),
      scope('ophub:pods:write', 'Pods: restart, scale, pause'),
      scope('ophub:db:read', 'Databases (read)'),
      scope('ophub:db:backup', 'Database backups'),
      scope('ophub:db:copy', 'Database copies'),
      scope('ophub:db:advise', 'Index advisor: scans, reviews, default set'),
      scope('ophub:auth:read', 'AUTH admin (read)'),
      scope('ophub:auth:write', 'AUTH admin (write)'),
      scope('ophub:scim:read', 'SCIM admin (read)'),
      scope('ophub:scim:write', 'SCIM admin (write)'),
      scope('ophub:stats:read', 'Statistics (read)'),
      scope('ophub:stats:admin', 'Statistics settings and reports'),
      scope('ophub:buddy:read', 'nyce.buddy statistics and admin pages (read)'),
      scope('ophub:buddy:admin', 'nyce.buddy administration'),
      scope('ophub:design:read', 'NYCE Design tenants (read)'),
      scope('ophub:design:write', 'NYCE Design tenants: create, enable/disable, edit'),
      scope('ophub:printqueue:read', 'Print queue jobs, printers and tokens (read)'),
      scope('ophub:printqueue:write', 'Print queue administration: re-queue, delete, tokens'),
      scope('ophub:automation:read', 'Automation gateway agents, routes, file watchers, logs (read)'),
      scope(
        'ophub:automation:write',
        'Automation gateway administration: agents, routes, file watchers, updates'
      ),
      scope('ophub:billing:read', 'Billing reports: run, download, history (read)'),
      scope('ophub:billing:write', 'Billing: schedules and sending reports'),
      scope('ophub:admin', 'Hub registry and audit log'),
    ],
  },
  {
    id: 'operator',
    label: 'Operator access to services',
    claims: [
      scope('auth:tenants:read', 'AUTH admin API (read)'),
      scope('auth:tenants:write', 'AUTH admin API (write)'),
      scope('scim:endpoints:read', 'SCIM gateway admin API (read)'),
      scope('scim:endpoints:write', 'SCIM gateway admin API (write)'),
      scope('datacollector:read', 'Datacollector admin API (read)'),
      scope('datacollector:admin', 'Datacollector admin API (write)'),
      scope('automation-gw:admin', 'Automation gateway admin API'),
      scope('printqueue:admin', 'Print queue admin API'),
      scope('nycelogic:auth-usage:read', 'Auth-method usage of every service'),
      scope('auth:aliases:read', 'Resolve a tenant’s alias (e.g. the datacollector’s own client)'),
      scope('config-tool:admin', 'Configuration Tool admin'),
    ],
  },
  {
    id: 'other',
    label: 'Other',
    claims: [
      scope('offline_access', 'Long-lived refresh chains for unattended user clients'),
      { type: 'role', value: 'admin', hint: 'roles claim: admin' },
    ],
  },
];

export const CLAIM_PRESETS: ClaimPreset[] = [
  {
    id: 'installation',
    label: 'WMS installation',
    hint: 'What a WorkServer’s service client needs',
    claims: [
      { type: 'scope', value: 'datacollector:write' },
      { type: 'scope', value: 'boxcalc:calculate' },
      { type: 'scope', value: 'printqueue:enqueue' },
      { type: 'scope', value: 'automation-gw:location:read' },
    ],
  },
  {
    id: 'design-user',
    label: 'NYCE Design user',
    hint: 'A customer tenant using NYCE Design (a role narrows users to design:warehousedesign:read)',
    claims: [{ type: 'scope', value: 'design:warehousedesign:*' }],
  },
  {
    id: 'operator',
    label: 'Operator',
    hint: 'NYCE operators: the hub and every service admin API',
    claims: [
      { type: 'scope', value: 'ophub:*' },
      { type: 'scope', value: 'auth:tenants:*' },
      { type: 'scope', value: 'design:tenants:*' },
      { type: 'scope', value: 'scim:endpoints:*' },
      { type: 'scope', value: 'datacollector:*' },
      { type: 'scope', value: 'automation-gw:admin' },
      { type: 'scope', value: 'printqueue:admin' },
      { type: 'scope', value: 'nycelogic:auth-usage:read' },
    ],
  },
];

export const claimKey = (c: ClaimInput) => `${c.type}\u0000${c.value}`;
export const sameClaim = (a: ClaimInput, b: ClaimInput) => a.type === b.type && a.value === b.value;

/** `list` plus every claim of `add` it does not hold yet. */
export const withClaims = (list: ClaimInput[], add: ClaimInput[]) => [
  ...list,
  ...add.filter((c, i) => !list.some((x) => sameClaim(x, c)) && add.findIndex((x) => sameClaim(x, c)) === i),
];
